import ApiUrl from 'enum/api-url.enum';
import httpService from 'services/http/http.service';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import errors from 'utils/messageList';

// Helper function to create consistent error responses (DRY principle)
function createErrorResponse(message, status = 412) {
  return {
    status: status,
    data: {
      details: message,
    },
  };
}

function getApiKeyVerification(device_id, organization_id) {
  console.log('Verifying API key for device:', device_id, 'organization:', organization_id);
  const url = `${ApiUrl.APIKEYVALIDATION}?serial_number=${device_id}&organization_id=${organization_id}`;

  return httpService
    .get(url)
    .then((response) => {
      console.log('API key verification response:', response);
      if (response.status === 200) {
        // Check if API key exists and is active
        if (!response.data?.apikey || !response.data?.active) {
          return Promise.reject(createErrorResponse(errors.organizationNotActivated));
        }

        Storages.MainStorage.setItem(StorageKeys.Keys.LICENSEAPIKEY, {
          api_key: response.data.apikey,
          organization_id: organization_id,
        });
        console.log('API key verified and stored successfully');
        return response;
      }

      // For non-200 status codes, also reject with structured error
      return Promise.reject(createErrorResponse(errors.organizationNotActivated));
    })
    .catch((error) => {
      console.error('API key verification error:', error);
      throw createErrorResponse(errors.organizationNotActivated);
    });
}

function getRegisterDeviceVerification(device_id, device_ip) {
  const data = getDeviceRegisterationData(device_id, device_ip);
  console.log('Sending device verification request:', {
    url: ApiUrl.REGISTERDEVICEVERIFICATION,
    data,
  });
  return httpService
    .post(`${ApiUrl.REGISTERDEVICEVERIFICATION}`, data)
    .then((response) => {
      console.log('Device verification response:', response);
      if (response.status >= 200 && response.status < 300) {
        // Validate device registration status
        if (!response?.data?.registered) {
          return Promise.reject(createErrorResponse(errors.deviceNotRegistered));
        }

        // Validate organization association
        const organizationId = response?.data?.organization_id?.id;
        if (!organizationId) {
          return Promise.reject(createErrorResponse(errors.deviceNotAssociated));
        }

        Storages.MainStorage.setItem(StorageKeys.Keys.DEVICEREGDATA, response?.data);

        // After successful device registration, attempt API key verification
        return getApiKeyVerification(device_id, organizationId).then(() => {
          // Register device for notifications after successful API key verification
          return registerNotificationDevice(device_id, organizationId).then(() => {
            return response;
          });
        });
      }
      return Promise.reject(createErrorResponse(errors.wentWrong));
    })
    .catch((error) => {
      console.error('Device verification error:', error);
      throw error;
    });
}

function putRegisterDeviceVerificationBySerialNumber(deviceId, ipAddress) {
  return httpService.put(ApiUrl.REGISTERDEVICETEST, getDeviceRegisterationData(deviceId, ipAddress));
}

function getDeviceRegisterationData(device_id, device_ip) {
  return {
    device_id: device_id,
    serial_number: device_id,
    version_name: '1.0',
    model: 'ANDROIDTV',
    sdk_version: '1',
    android: false,
    ip: device_ip,
  };
}
function getDevices(device_id) {
  return httpService.get(ApiUrl.DEVICES + '?device_id=' + device_id);
}

function registerNotificationDevice(device_id, organization_id) {
  console.log('Registering notification device:', device_id, 'for organization:', organization_id);

  const data = {
    type: 'android',
    device_id: device_id,
    device_type: 'ANDROIDTV',
  };

  // Helper function to handle device registration
  const registerDevice = () => {
    return httpService
      .post(ApiUrl.NOTIFICATION_SUBSCRIPTION, data)
      .then((response) => {
        console.log('Notification device registration response:', response);

        if (response.status === 201) {
          console.log('Notification device registered successfully');
          return response;
        }

        console.error('Notification device registration failed with status:', response.status);
        return Promise.reject(createErrorResponse(errors.wentWrong));
      })
      .catch((error) => {
        console.error('Notification device registration error:', error);
        return Promise.reject(error); // Throw error to block the registration flow
      });
  };

  return new Promise((resolve, reject) => {
    httpService
      .get(ApiUrl.NOTIFICATION_SUBSCRIPTION + '?device_id=' + device_id)
      .then((registered) => {
        // If device exists but is not active, reject
        if (registered?.data && !registered.data.active) {
          reject(createErrorResponse(errors.deviceNotActive, 423));
          return;
        }
        registerDevice().then(resolve).catch(reject);
      })
      .catch(() => {
        // For any GET error, try registration
        registerDevice().then(resolve).catch(reject);
      });
  });
}

function deleteNotificationDevice(device_id) {
  console.log('Deleting notification device:', device_id);

  const data = {
    device_ids: device_id,
  };

  return httpService
    .deleteReq(ApiUrl.DEVICES_LIST, data)
    .then((response) => {
      console.log('Notification device deletion response:', response);
      return response;
    })
    .catch((error) => {
      console.error('Notification device deletion error:', error);
      throw createErrorResponse(errors.wentWrong);
    });
}

function markDeviceAliveStatus(alive) {
  const device = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICEREGDATA);

  if (!device?.id) {
    return Promise.reject(createErrorResponse(errors.deviceNotRegistered));
  }

  const data = {
    id: device.id,
    alive: alive,
  };

  return httpService.put(ApiUrl.REGISTERDEVICE, data);
}

const obj = {
  getRegisterDeviceVerification,
  putRegisterDeviceVerificationBySerialNumber,
  getDevices,
  registerNotificationDevice,
  deleteNotificationDevice,
  markDeviceAliveStatus,
};

export default obj;
